import { Box, Switch } from "@mui/material";
import { extractErrorMessage, useApi } from "@platform/api";
import { cellClassName, DataTableV2 } from "@platform/design-system";
import { User } from "@platform/types";
import { useMutation } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState, type ReactElement } from "react";
import { AgGridReact } from "ag-grid-react";

import useToastNotificationService from "../hooks/useToastNotificationService";
import { submitEvent } from "../utils/tracking";
import { PAGINATION_PAGE_SIZE_OPTIONS } from "../constants";

import type {
  ColDef,
  GetRowIdParams,
  ICellRendererParams,
} from "ag-grid-community";

const DEFAULT_PAGE_SIZE = 10;

interface UsersTableContext {
  pendingIds: Set<string>;
  onToggle: (user: User) => void;
}

const ActiveCell = ({
  data,
  context,
}: ICellRendererParams<User, boolean, UsersTableContext>): ReactElement => (
  <Switch
    color="brandPrimary"
    data-testid={`user-status-switch-${data!.id}`}
    onClick={() => context.onToggle(data!)}
    checked={data!.accountEnabled}
    disabled={context.pendingIds.has(data!.id)}
  />
);

interface Props {
  data?: User[];
}

const UsersTable = ({ data = [] }: Props): ReactElement => {
  const [rows, setRows] = useState<User[]>(data);
  const [pendingIds, setPendingIds] = useState<Set<string>>(new Set());
  const axiosInstance = useApi();
  const gridRef = useRef<AgGridReact>(null);
  const { createToastNotification } = useToastNotificationService();

  useEffect(() => {
    setRows(data);
  }, [data]);

  const setAccountEnabled = (id: string, accountEnabled: boolean): void => {
    setRows((prev) =>
      prev.map((user) => (user.id === id ? { ...user, accountEnabled } : user)),
    );
  };

  const { mutate: changeStatus } = useMutation({
    mutationFn: async ({
      id,
      accountEnabled,
    }: {
      id: string;
      accountEnabled: boolean;
    }): Promise<void> => {
      await axiosInstance.post(
        `/brokerportal/v1/${accountEnabled ? "disableUser" : "enableUser"}`,
        { userId: id },
      );
    },
    onMutate: ({ id, accountEnabled }) => {
      setPendingIds((prev) => new Set(prev).add(id));
      const previousAccountEnabled = accountEnabled;
      setAccountEnabled(id, !accountEnabled);
      return { previousAccountEnabled };
    },
    onSuccess: (_data, { accountEnabled }) => {
      createToastNotification({
        severity: "success",
        message: `User ${accountEnabled ? "disabled" : "enabled"} successfully`,
      });
    },
    onError: (err, { id }, context) => {
      if (context) {
        setAccountEnabled(id, context.previousAccountEnabled);
      }
      const msg = extractErrorMessage(
        err,
        "An error occurred changing the status",
      );
      submitEvent("Error Viewed", {
        error_type: "user_status_change",
        error_reason: msg,
      });
      createToastNotification({
        severity: "error",
        message: `Failed to change user status`,
      });
    },
    onSettled: (_data, _err, { id }) => {
      setPendingIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    },
  });

  const columns = useMemo<ColDef<User>[]>(
    () => [
      {
        flex: 1.25,
        field: "givenName",
        headerName: "First name",
        filter: false,
        suppressHeaderMenuButton: true,
        sort: "asc",
      },
      {
        flex: 1.25,
        field: "surname",
        headerName: "Last name",
        suppressHeaderMenuButton: true,
        filter: false,
      },
      {
        flex: 2,
        field: "mail",
        headerName: "Email",
        suppressHeaderMenuButton: true,
        filter: false,
      },
      {
        flex: 0.5,
        colId: "active",
        headerName: "Active",
        suppressHeaderMenuButton: true,
        filter: false,
        resizable: false,
        cellRenderer: ActiveCell,
      },
    ],
    [],
  );

  const context = useMemo<UsersTableContext>(
    () => ({
      pendingIds,
      onToggle: (user: User) =>
        changeStatus({
          id: user.id,
          accountEnabled: user.accountEnabled,
        }),
    }),
    [pendingIds, changeStatus],
  );

  // AG Grid does not refresh cells when only `context` changes, so force the
  // active cell to re-render when the pending rows change.
  useEffect(() => {
    const api = gridRef.current?.api;
    if (api && !api.isDestroyed()) {
      api.refreshCells({ columns: ["active"], force: true });
    }
  }, [pendingIds]);

  return (
    <Box
      data-testid="users-table"
      sx={{
        "& .ag-center-cols-viewport": {
          minHeight:
            rows.length === 0 ? "150px !important" : "unset !important",
        }, // AG Grid sets a min height on the viewport that can cause the table to be taller than its content, so unset it when there are rows
        [`& .${cellClassName} .ag-cell-wrapper`]: {
          display: "grid",
          alignItems: "center",
          minHeight: "100%",
        },
      }}
    >
      <DataTableV2
        ref={gridRef}
        rowData={rows}
        columnDefs={columns}
        context={context}
        getRowId={(params: GetRowIdParams<User>) => params.data.id}
        overlayNoRowsTemplate="No users found"
        pagination={true}
        paginationPageSize={DEFAULT_PAGE_SIZE}
        paginationPageSizeSelector={PAGINATION_PAGE_SIZE_OPTIONS}
        autoSizeStrategy={undefined}
      />
    </Box>
  );
};

export default UsersTable;
